import { GUEST_AGE_HELP, guestLimitMessage, capPortalGuests } from "../../utils/guestRules";
import React, { useEffect, useRef, useState } from "react";
import { PACKAGE_GUEST_LIMITS, normalizePackageGuests } from "../../utils/packageGuestPolicy";


const Row = ({ label, value, setValue, min = 0, max = 16, helper = GUEST_AGE_HELP[label], largeControls = false }) => (
  <div className="flex items-center justify-between gap-3">
    <div className="min-w-0">
      <div className="text-sm font-semibold text-[#10324d]">{label}</div>
      {helper ? <div className="mt-0.5 text-xs leading-4 text-[#5b6a7d]">{helper}</div> : null}
    </div>
    <div className="inline-flex items-center gap-2">
      <button
        type="button"
        onClick={() => setValue(Math.max(min, value - 1))}
        className={`${largeControls ? "h-11 w-11" : "h-8 w-8"} rounded-full border border-[#ccd7e2] text-[#10324d] disabled:opacity-40`}
        disabled={value <= min}
        aria-label={`Decrease ${label}`}
      >
        -
      </button>
      <span className="w-6 text-center text-sm font-semibold text-[#10324d]" role="status" aria-live="polite" aria-atomic="true" aria-label={`${label} count`}>{value}</span>
      <button
        type="button"
        onClick={() => setValue(Math.min(max, value + 1))}
        className={`${largeControls ? "h-11 w-11" : "h-8 w-8"} rounded-full border border-[#ccd7e2] text-[#10324d] disabled:opacity-40`}
        disabled={value >= max}
        aria-label={`Increase ${label}`}
      >
        +
      </button>
    </div>
  </div>
);

const GuestMixSelector = ({
  value,
  onChange,
  onOpenRequest,
  maxGuests: requestedMaxGuests = PACKAGE_GUEST_LIMITS.maxGuests,
  minAdults = 1,
  maxAdults = PACKAGE_GUEST_LIMITS.maxAdults,
  maxChildren = PACKAGE_GUEST_LIMITS.maxChildren,
  maxInfants = PACKAGE_GUEST_LIMITS.maxInfants,
  variant = "standalone",
  prefixIcon,
  expanded = false,
  largeControls = false,
}) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  const maxGuests = capPortalGuests(requestedMaxGuests);
  const limits = { maxGuests, minAdults, maxAdults, maxChildren, maxInfants };
  const { adults, children, infants } = normalizePackageGuests(value, limits);
  const totalGuests = adults + children + infants;
  const adultMax = Math.max(minAdults, Math.min(maxAdults, maxGuests - children - infants));

  useEffect(() => {
    if (expanded) return undefined;
    const onClick = (event) => {
      if (!ref.current) return;
      if (!ref.current.contains(event.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [expanded]);

  const setAdults = (next) => onChange?.(normalizePackageGuests({ adults: next, children, infants }, limits));
  const setChildren = (next) => onChange?.(normalizePackageGuests({ adults, children: next, infants }, limits));
  const setInfants = (next) => onChange?.(normalizePackageGuests({ adults, children, infants: next }, limits));

  const label =
    totalGuests > 0
      ? `${totalGuests} guest${totalGuests > 1 ? "s" : ""}`
      : variant === "inline"
        ? "Who"
        : "Guests";
  const buttonClassName =
    variant === "inline"
      ? "flex w-full items-center gap-1 bg-transparent px-0 py-0 text-left text-xs text-[#0f172a] hover:text-[#0f172a] sm:gap-2 sm:text-sm"
      : "flex w-full items-center gap-2 rounded-xl border border-[#ccd7e2] bg-white px-4 py-2.5 text-left text-sm text-[#10324d] hover:border-[#10324d]";
  const isOpen = expanded ? true : open;
  const panelClassName = expanded
    ? "mt-2 w-full rounded-2xl border border-[#dfeaf4] bg-white p-4"
    : "absolute z-40 mt-2 w-[320px] rounded-2xl border border-[#dfeaf4] bg-white p-4 shadow-[0_24px_70px_rgba(16,50,77,0.16)]";

  return (
    <div className="relative" ref={ref}>
      {expanded ? null : (
        <button
          type="button"
          onClick={() => {
            if (onOpenRequest) {
              onOpenRequest();
              return;
            }
            setOpen((prev) => !prev);
          }}
          className={buttonClassName}
          aria-expanded={open}
        >
          {prefixIcon ? <span className="inline-flex items-center justify-center">{prefixIcon}</span> : null}
          <span className="font-medium">{label}</span>
        </button>
      )}

      {isOpen ? (
        <div className={panelClassName}>
          <div className="space-y-4">
            <Row label="Adults" value={adults} setValue={setAdults} min={minAdults} max={adultMax} largeControls={largeControls} />
            <Row
              label="Children"
              value={children}
              setValue={setChildren}
              min={0}
              max={Math.max(0, Math.min(maxChildren, maxGuests - adults - infants))}
              largeControls={largeControls}
            />
            <Row
              label="Infants"
              value={infants}
              setValue={setInfants}
              min={0}
              max={Math.max(0, Math.min(maxInfants, maxGuests - adults - children))}
              largeControls={largeControls}
            />
            <p className="rounded-xl bg-[#f8fafc] px-3 py-2 text-xs leading-5 text-[#5b6a7d]">
              {guestLimitMessage(maxGuests, maxAdults)}
            </p>
          </div>
          {expanded ? null : (
            <div className="mt-3 flex justify-end">
              <button
                type="button"
                className="rounded-full border border-[#ccd7e2] px-4 py-1.5 text-sm font-semibold text-[#10324d]"
                onClick={() => setOpen(false)}
              >
                Done
              </button>
            </div>
          )}
        </div>
      ) : null}
    </div>
  );
};

export default GuestMixSelector;
